import { useState } from "react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";

// IST is a fixed UTC+5:30 offset (no DST) — same convention CalendarPage.jsx
// already uses for its own day-bucketing (toLocaleDateString with
// timeZone: "Asia/Kolkata"). Appending "T00:00:00+05:30" to that calendar
// date gives an ISO instant the backend's core.resolve_report_date_range
// parses correctly as IST midnight, regardless of the browser's own
// timezone.
const istDateStr = (d) => d.toLocaleDateString("en-CA", { timeZone: "Asia/Kolkata" });
const istMidnightIso = (dateStr) => `${dateStr}T00:00:00+05:30`;

const PRESETS = [
  ["today", "Today"], ["yesterday", "Yesterday"],
  ["7", "Last 7 days"], ["30", "Last 30 days"],
  ["90", "Last 90 days"], ["365", "Last year"],
  ["this_month", "This month"], ["previous_month", "Previous month"],
  ["custom", "Custom range"],
];

// 2026-09 Phase 2 (F2) — one reusable Reports date-range selector.
// Calls onChange({preset, days, date_from, date_to}) — exactly one of
// `days` or the date_from/date_to pair is meaningful, matching the
// backend's core.resolve_report_date_range contract (explicit dates win
// over days). Every preset here resolves to real IST calendar boundaries,
// never the browser's local timezone.
export const ReportDateFilter = ({ preset = "30", onChange, testIdPrefix = "report-date" }) => {
  const [customFrom, setCustomFrom] = useState("");
  const [customTo, setCustomTo] = useState("");

  const apply = (p) => {
    const todayStr = istDateStr(new Date());
    if (p === "today") {
      onChange({ preset: p, date_from: istMidnightIso(todayStr), date_to: istMidnightIso(todayStr) });
    } else if (p === "yesterday") {
      const y = new Date(Date.now() - 86400000);
      const yStr = istDateStr(y);
      onChange({ preset: p, date_from: istMidnightIso(yStr), date_to: istMidnightIso(yStr) });
    } else if (p === "7" || p === "30" || p === "90" || p === "365") {
      onChange({ preset: p, days: p });
    } else if (p === "this_month") {
      const [y, m] = todayStr.split("-");
      onChange({ preset: p, date_from: istMidnightIso(`${y}-${m}-01`), date_to: istMidnightIso(todayStr) });
    } else if (p === "previous_month") {
      const [y, m] = todayStr.split("-").map(Number);
      let py = y, pm = m - 1;
      if (pm === 0) { pm = 12; py = y - 1; }
      const pStart = `${py}-${String(pm).padStart(2, "0")}-01`;
      // Calendar day-count only — safe in local time regardless of the
      // browser's own timezone, since a month's length doesn't depend on it.
      const lastDay = new Date(py, pm, 0).getDate();
      const pEnd = `${py}-${String(pm).padStart(2, "0")}-${String(lastDay).padStart(2, "0")}`;
      onChange({ preset: p, date_from: istMidnightIso(pStart), date_to: istMidnightIso(pEnd) });
    } else if (p === "custom") {
      onChange({ preset: p, date_from: customFrom ? istMidnightIso(customFrom) : null,
                date_to: customTo ? istMidnightIso(customTo) : null });
    }
  };

  // Custom range's two date inputs fire onChange independently — apply()'s
  // captured customFrom/customTo would be stale by one render if called
  // from inside the same handler that just updated one of them, so this
  // builds the payload directly from the fresh values instead.
  const applyCustom = (from, to) => {
    onChange({ preset: "custom", date_from: from ? istMidnightIso(from) : null,
              date_to: to ? istMidnightIso(to) : null });
  };

  return (
    <div className="flex items-center gap-2 flex-wrap">
      <Select value={preset} onValueChange={apply}>
        <SelectTrigger data-testid={`${testIdPrefix}-preset`} className="h-9 w-40 text-xs"><SelectValue /></SelectTrigger>
        <SelectContent>
          {PRESETS.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
        </SelectContent>
      </Select>
      {preset === "custom" && (
        <>
          <Input data-testid={`${testIdPrefix}-from`} type="date" className="h-9 w-36 text-xs"
            value={customFrom} onChange={(e) => { setCustomFrom(e.target.value); if (e.target.value && customTo) applyCustom(e.target.value, customTo); }} />
          <span className="text-xs text-muted-foreground">to</span>
          <Input data-testid={`${testIdPrefix}-to`} type="date" className="h-9 w-36 text-xs"
            value={customTo} onChange={(e) => { setCustomTo(e.target.value); if (customFrom && e.target.value) applyCustom(customFrom, e.target.value); }} />
        </>
      )}
    </div>
  );
};
